import { TranslatePipe } from "@ngx-translate/core";
import { Apollo } from "apollo-angular";
import dayjs from "dayjs";
import { firstValueFrom } from "rxjs";

import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  resource,
  signal,
  untracked,
  viewChild,
} from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";

import { BaseSubscriptionsOfTypeQueryQuery } from "../../../apis/networkdashboard/graphql/graphql";
import { BASE_SUBSCRIPTIONS_OF_TYPE_QUERY } from "../../../apis/networkdashboard/static/graphql-queries/subscription";
import { ProductType } from "../../../helpers/enums/productType";
import { AuthService } from "../../../services/authservice";
import { MessageService } from "../../../services/messageservice";
import { MalfunctionFilterImpact } from "./components/malfunction-filter-impact/malfunction-filter-impact";
import { MalfunctionFilterPeriod } from "./components/malfunction-filter-period/malfunction-filter-period";
import { MalfunctionFilterService } from "./components/malfunction-filter-service/malfunction-filter-service";
import { MalfunctionFilterType } from "./components/malfunction-filter-type/malfunction-filter-type";

export type SubscriptionItem = BaseSubscriptionsOfTypeQueryQuery["customerSubscriptions"][number];

@Component({
  selector: "malfunction-filter",
  templateUrl: "malfunction-filter.html",
  styleUrl: "malfunction-filter.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MalfunctionFilterImpact,
    MalfunctionFilterPeriod,
    MalfunctionFilterService,
    MalfunctionFilterType,
    TranslatePipe,
  ],
})
export class MalfunctionFilter {
  private msgsrv = inject(MessageService);
  private auth = inject(AuthService);
  private apollo = inject(Apollo);

  private filterType = viewChild.required(MalfunctionFilterType);
  private filterImpact = viewChild.required(MalfunctionFilterImpact);

  presetSubscriptionId = input.required<string>();
  presetProductType = input.required<string>();

  protected mobileFilterActive = signal(false);
  protected loadMoreActive = signal(false);
  protected hasMore = signal(true);
  protected initialStartDate = signal(new Date());
  protected initialEndDate = signal(new Date());
  protected productType = signal("");
  protected subscription = signal<SubscriptionItem | undefined>(undefined);

  private userLoaded = toSignal(this.auth.userLoaded, { initialValue: false });

  private subscriptionsResource = resource({
    params: () => (this.userLoaded() ? { customerId: this.auth.viewingCustomer().customerId } : undefined),
    loader: ({ params }) =>
      firstValueFrom(
        this.apollo.query<BaseSubscriptionsOfTypeQueryQuery>({
          query: BASE_SUBSCRIPTIONS_OF_TYPE_QUERY,
          variables: { customerId: params.customerId, products: this.productTypes, statuses: ["active"] },
        }),
      ).then((result) => result.data?.customerSubscriptions ?? []),
  });

  private readonly allSubscriptions = computed(() => this.subscriptionsResource.value() ?? []);
  protected readonly subscriptionList = computed(() =>
    Object.fromEntries(
      this.productTypes.map((type) => [type, this.allSubscriptions().filter((sub) => sub.product.type === type)]),
    ),
  );

  protected readonly productTypes = [
    ProductType.IP,
    ProductType.LightPath,
    ProductType.L2VPN,
    ProductType.L3VPN,
    ProductType.Firewall,
    ProductType.Port,
  ];
  private loadNewTimeFilterPreset = true;

  constructor() {
    effect(() => {
      this.presetSubscriptionId();
      untracked(() => {
        this.loadNewTimeFilterPreset = true;
        this.selectPresets();
      });
    });

    effect(() => {
      this.presetProductType();
      untracked(() => {
        this.loadNewTimeFilterPreset = true;
        this.triggerFilter();
        this.selectPresets();
      });
    });

    effect(() => {
      this.allSubscriptions();
      untracked(() => this.selectPresets());
    });

    this.resetTimeFilter();

    this.msgsrv.malfunctionFilterDate$.pipe(takeUntilDestroyed()).subscribe((data) => {
      this.initialStartDate.set(data.filterDate);
      this.initialEndDate.set(dayjs(data.filterDate).endOf("day").toDate());
      this.triggerFilter();
    });

    this.msgsrv.malfunctionLoadMore$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.loadMore();
    });
  }

  private loadMore() {
    this.loadMoreActive.set(true);

    // paginate by month
    this.initialEndDate.set(new Date(this.initialStartDate()));
    const newStart = new Date(this.initialStartDate());
    newStart.setFullYear(newStart.getFullYear() - 1);
    this.initialStartDate.set(newStart);
    this.triggerFilter();
  }

  private selectPresets() {
    if (this.presetSubscriptionId() === "") {
      return;
    }

    this.productType.set(this.presetProductType());

    const subscriptionFound = this.subscriptionList()[this.productType()]?.find(
      (value) => value.subscriptionId === this.presetSubscriptionId(),
    );

    if (subscriptionFound !== undefined) {
      this.subscription.set(subscriptionFound);
      this.triggerFilter();
    }
  }

  protected toggleFilter() {
    this.mobileFilterActive.set(!this.mobileFilterActive());
  }

  protected resetAllFilters() {
    this.productType.set("");
    this.subscription.set(undefined);
    this.resetTimeFilter();
    this.loadMoreActive.set(false);
    this.loadNewTimeFilterPreset = true;
    this.filterType().reset();
    this.filterImpact().reset();
    this.triggerFilter();
  }

  private resetTimeFilter() {
    if (!this.subscription() && !this.loadMoreActive()) {
      if (this.loadNewTimeFilterPreset) {
        this.loadNewTimeFilterPreset = false;

        const start = new Date();
        start.setFullYear(start.getFullYear() - 1);
        this.initialStartDate.set(start);

        const end = new Date();
        end.setMonth(end.getMonth() + 1);
        this.initialEndDate.set(end);
      }
    }

    if (this.subscription() && this.loadNewTimeFilterPreset) {
      this.loadNewTimeFilterPreset = false;
      const start = new Date();
      start.setFullYear(start.getFullYear() - 1);
      this.initialStartDate.set(start);
    }
  }

  protected productTypeChanged() {
    this.subscription.set(undefined);
    this.loadMoreActive.set(false);
    if (this.productType() === "") {
      this.loadNewTimeFilterPreset = true;
    }
    this.triggerFilter();
  }

  protected checkBeforeTriggerFilter() {
    this.loadNewTimeFilterPreset = true;
    this.triggerFilter();
  }

  protected triggerFilter() {
    // reset the time filter if no subscription is selected AND the load more function is not triggerd
    this.resetTimeFilter();

    const subscription = this.subscription();
    const msg = {
      filterStartDate: this.initialStartDate(),
      filterEndDate: this.initialEndDate(),
      productType: this.productType(),
      subscriptionId: subscription?.subscriptionId ?? "",
      subscriptionName: subscription?.customerDescription ?? subscription?.description ?? "",
      loadMoreActive: this.loadMoreActive(),
      hasMore: this.hasMore(),
    };

    this.msgsrv.enableMalfunctionFilter(msg);
  }
}
